"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Calendar, Sparkles, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function BringTheHeatToYourEvent() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: btnRef,
    isVisible: btnVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [offsetY, setOffsetY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setOffsetY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="240430" id="bring-the-heat-to-your-event" className="relative min-h-[90vh] w-full overflow-hidden bg-background text-foreground flex items-center pt-24 pb-16 lg:pt-32 lg:pb-24">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-lg bg-primary/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          
          {}
          <div className="w-full lg:w-[60%] flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <div ref={titleRef} className={`transition-all duration-1000 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-bold mb-6 tracking-widest uppercase">
                  <Sparkles className="w-4 h-4" />
                  <span>The Rolling Experience</span>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl xl:text-[100px] font-bold font-default leading-[0.9] text-foreground tracking-tighter uppercase">
                  WE BRING THE <br className="hidden md:block" />
                  <span className="text-primary relative inline-block">
                    PARTY
                    <svg className="absolute w-full h-3 -bottom-2 left-0 text-primary/40 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                </h1>
              </div>

              <p ref={textRef} className={`text-lg md:text-xl text-muted-foreground max-w-2xl font-default leading-relaxed transition-all duration-1000 delay-200 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                Rolling Tacos isn't just a meal—it's a high-energy event. From custom street-food menus to curated music and urban vibes, we turn every location into the ultimate celebration.
              </p>
            </div>

            <div ref={btnRef} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-1000 delay-400 ease-out ${btnVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Button size="lg" className="h-14 px-8 text-lg rounded-md shadow-lg shadow-primary/40 font-bold uppercase" asChild>
                <Link to="/TruckBookingPage">
                  <SafeHydrate><Calendar className="w-5 h-5 mr-2" /></SafeHydrate>
                  Book the Truck
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-lg rounded-md bg-background/50 backdrop-blur-md border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold uppercase" asChild>
                <Link to="/FullMenuPage">
                  View Full Menu
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            {}
            <div className={`pt-8 flex items-center gap-6 transition-all duration-1000 delay-500 ease-out ${btnVisible ? "opacity-100" : "opacity-0"}`}>
              <div className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                <span className="text-primary font-bold">10k+</span> Tacos Served This Month
              </div>
            </div>
          </div>

          {}
          <div className="w-full lg:w-[40%] relative mt-12 lg:mt-0">
            <div ref={imgRef} className={`relative w-full max-w-md mx-auto lg:max-w-none transition-all duration-1000 delay-300 ease-out ${imgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{
            transform: `translateY(${offsetY * 0.05}px)`
          }}>
              {}
              <div className="relative animate-[float_6s_ease-in-out_infinite]">
                {}
                <div className="relative aspect-[4/5] w-full rounded-md overflow-hidden shadow-2xl shadow-primary/20 border-2 border-primary/50 backdrop-blur-md">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/rolling_tacos_truck_wedding_party.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/rolling_tacos_truck_wedding_party.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685947/img/rolling_tacos_truck_wedding_party.png-wvc-sizes" alt="Rolling Tacos truck surrounded by a high-energy crowd at a wedding" className="w-full h-full object-cover object-center grayscale-[20%] hover:grayscale-0 transition-all duration-500" loading="lazy" />
                  
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
                </div>

                {}
                <div className="absolute -bottom-6 -left-6 bg-card rounded-md p-4 shadow-xl border-2 border-primary flex items-center gap-4 animate-[float_5s_ease-in-out_infinite_reverse]">
                  <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center text-2xl">
                    
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground font-default uppercase tracking-tight">Habanero Heat</p>
                    <p className="text-xs text-primary font-mono">LIVE DJ ON BOARD</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
      `
    }} />
    </section>;
}